// src/components/LanguageToggle.tsx

import { useState, useRef, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import 'flag-icons/css/flag-icons.min.css';
import { motion, AnimatePresence } from 'framer-motion';

interface LanguageToggleProps {
  hideDropdown?: boolean;
  onClick?: () => void;
}

export function LanguageToggle({ hideDropdown = false, onClick }: LanguageToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { language, setLanguage, isTransitioning } = useLanguage();

  const languages = {
    en: 'English',
    is: 'Íslenska',
  };
  const languageToFlag = {
    en: 'gb',
    is: 'is',
  };

  const handleLanguageChange = (code: keyof typeof languages) => {
    if (code === language) return;

    setIsChanging(true);
    setIsOpen(false);

    // Add visual feedback during transition
    setTimeout(() => {
      setLanguage(code, true);
      setTimeout(() => {
        setIsChanging(false);
      }, 200);
    }, 50);
  };

  const handleButtonClick = () => {
    if (hideDropdown && onClick) {
      onClick();
    } else {
      setIsOpen(!isOpen);
    }
  };

  // Close if clicking outside
  useEffect(() => {
    if (hideDropdown) return; // Don't set up click outside listener if dropdown is hidden

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, hideDropdown]);

  return (
    <div className="relative" ref={dropdownRef} style={{ zIndex: 100000 }}>
      <motion.button
        onClick={handleButtonClick}
        className={`w-[40px] h-[40px] flex items-center justify-center rounded-full border border-gray-200/30 bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20 shadow-sm ${
          isChanging || isTransitioning ? 'opacity-70 cursor-wait' : ''
        }`}
        aria-label="Change language"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        disabled={isChanging || isTransitioning}
      >
        <motion.div
          animate={{
            rotate: isOpen ? 180 : 0,
            scale: isChanging ? 0.9 : 1,
          }}
          transition={{ duration: 0.3, ease: 'anticipate' }}
        >
          <Globe className={`w-5 h-5 transition-colors ${isChanging ? 'text-blue-600' : ''}`} />
        </motion.div>
      </motion.button>

      {!hideDropdown && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed top-full right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl py-3 border border-gray-100"
              style={{
                zIndex: 100001,
                position: 'fixed',
                top: '60px',
                right: '20px',
              }}
            >
              {Object.entries(languages).map(([code, name]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code as keyof typeof languages)}
                  className={`w-full px-4 py-2.5 text-left flex items-center gap-4 transition-colors ${
                    language === code
                      ? 'text-gray-900 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50/80'
                  } ${isChanging || isTransitioning ? 'pointer-events-none opacity-50' : ''}`}
                  disabled={isChanging || isTransitioning}
                >
                  <motion.div
                    className="flex items-center gap-4 w-full"
                    whileHover={{ x: 4 }}
                    transition={{ type: 'tween', duration: 0.15 }}
                  >
                    <motion.span
                      className="relative w-7 h-7 rounded-full overflow-hidden border border-gray-100 shadow-sm bg-white flex items-center justify-center"
                      animate={{
                        scale: language === code && isChanging ? 1.1 : 1,
                        rotate: language === code && isChanging ? 360 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <span
                        className={`fi fi-${languageToFlag[code]} fis`}
                        style={{
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          lineHeight: 0,
                        }}
                      />
                    </motion.span>
                    <span className="text-[15px]">{name}</span>
                  </motion.div>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
